import { useState, useCallback, useEffectEvent, useEffect, useMemo } from 'react';
import {
  BrainCircuit,
  FileText,
  Sparkles,
  AlertCircle,
  Loader2,
  FileUp,
  X,
  Info,
  ChevronLeft,
  ChevronRight,
  Download,
  XCircle,
  UploadCloud,
  BadgeCheck,
  FileCode,
} from 'lucide-react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { retry, sha256 } from '@/libs/utils';
import { getJDs, type GetJobDescriptionsResponseItem } from '@/apis/jobDescriptionsApis';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  getDownloadCVUrl,
  getUploadCv,
  postUploadCV,
  putUploadBlob,
  type ListResumesCandidatesResponseItem,
  type ResumesCandidatesStatus,
  type UploadCVBody,
} from '../../apis/AICVScannerApis';

const PAGE_SIZE = 5;
const TIME_INTERVAL = 5000;

const SOURCE_OPTIONS = [
  'Apply',
  'Hunt',
  'Search',
  'Internal Referral',
  'Partner',
  'Other',
] as const;
const CHANNEL_OPTIONS = [
  'TopCV',
  'IT Việc',
  'Facebook',
  'LinkedIn',
  'Zalo',
  'Website',
  'Network',
  'Internal Referral',
  'Fanpage',
  'Headhunt',
  'VietCV',
  'Partner',
  'Other',
] as const;

type UploadStatus = 'READY' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
type VisiblePageItem = number | 'ellipsis';

const processingStatusStyles: Record<ResumesCandidatesStatus, string> = {
  PENDING: 'bg-muted text-muted-foreground',
  DUPLICATED: 'bg-info-muted text-info-muted-foreground',
  SCORED: 'bg-success-muted text-success-muted-foreground',
  FAILED: 'bg-error-muted text-error-muted-foreground',
};

export const getProcessingStatusLabel = (status: ResumesCandidatesStatus) => {
  if (status === 'PENDING') {
    return 'Processing';
  }

  return status;
};

export const isProcessingStatus = (status: ResumesCandidatesStatus) => status === 'PENDING';

/** Short, friendly text for the progress table; raw API text is for engineers. */
function userFacingProcessingNote(raw: string | null | undefined) {
  const r = raw?.trim();
  if (!r) {
    return 'We couldn’t read this CV. Try uploading it again, or use PDF or Word if you used another app.';
  }
  if (r.includes('not available in storage') || r.includes('5 seconds')) {
    return 'The file didn’t finish uploading in time. Check your connection and upload the CV again.';
  }
  if (r.includes('Unsupported file type for text extraction')) {
    return 'This file type isn’t supported. Please use PDF, Word (DOCX), or a plain .txt file.';
  }
  if (r.includes('did not contain any extractable text')) {
    return 'We couldn’t find any readable text. The file may be empty, a scan without text, or a protected document.';
  }
  if (r.startsWith('Extraction process failed:') || r.includes('Extraction process failed')) {
    return 'We couldn’t open or read this file. Try exporting a new PDF or DOCX, then upload again.';
  }
  return 'We couldn’t finish reading this CV. Please try a different file or contact support if this keeps happening.';
}

export function getUploadRejectionMessage(fileRejections: FileRejection[]) {
  for (const rejection of fileRejections) {
    if (rejection.errors.some((error) => error.code === 'file-too-large')) {
      return 'File size must not exceed 10MB.';
    }
  }

  for (const rejection of fileRejections) {
    if (rejection.errors.some((error) => error.code === 'file-too-small')) {
      return 'Uploaded file is empty. Please select a valid file.';
    }
  }

  for (const rejection of fileRejections) {
    if (rejection.errors.some((error) => error.code === 'file-invalid-type')) {
      return 'Unsupported file type. Please upload PDF, DOCX, TXT, or XLSX files.';
    }
  }

  for (const rejection of fileRejections) {
    if (rejection.errors.some((error) => error.code === 'too-many-files')) {
      return 'You can upload a maximum of 50 files.';
    }
  }

  return 'We could not upload one or more files. Please try again.';
}

export function getUserFacingUploadErrorMessage(error: unknown) {
  const rawMessage = error instanceof Error ? error.message : '';

  if (rawMessage.includes('File size must be a positive number')) {
    return 'Uploaded file is empty. Please select a valid file.';
  }

  return rawMessage || 'Failed to upload CVs to blob storage.';
}

export function getVisibleProcessingPages(
  currentPage: number,
  totalPages: number,
): VisiblePageItem[] {
  if (totalPages <= 0) {
    return [];
  }

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  const normalizedPages = [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);

  const visibleItems: VisiblePageItem[] = [];

  for (const page of normalizedPages) {
    const lastItem = visibleItems[visibleItems.length - 1];
    if (typeof lastItem === 'number' && page - lastItem > 1) {
      visibleItems.push('ellipsis');
    }
    visibleItems.push(page);
  }

  return visibleItems;
}

export default function AICVScanner() {
  const [jds, setJds] = useState<GetJobDescriptionsResponseItem[]>([]);
  const [isLoadingJds, setIsLoadingJds] = useState<boolean>(false);
  const [files, setFiles] = useState<
    {
      uuid: string;
      file: File;
    }[]
  >([]);
  const [statusMap, setStatusMap] = useState<Record<string, UploadStatus>>({});
  const [selectedJdId, setSelectedJdId] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [processingPage, setProcessingPage] = useState(1);
  const [processingCvs, setProcessingCvs] = useState<ListResumesCandidatesResponseItem[]>([]);
  const [isLoadingProcessingCvs, setIsLoadingProcessingCvs] = useState(false);
  const [totalProcessingCvs, setTotalProcessingCvs] = useState(0);
  const totalProcessingPages = useMemo(
    () => Math.ceil(totalProcessingCvs / 5),
    [totalProcessingCvs],
  );
  const visibleProcessingPages = useMemo(
    () => getVisibleProcessingPages(processingPage, totalProcessingPages),
    [processingPage, totalProcessingPages],
  );

  useEffect(() => {
    const fetchJDs = async () => {
      if (isLoadingJds) return;
      try {
        setIsLoadingJds(true);
        const response = await getJDs({
          page: 1,
          page_size: 100,
          status: 'ACTIVE',
        });
        setJds(response?.items || []);
      } catch (e) {
        console.error(e);
        // setJds([])
      } finally {
        setIsLoadingJds(false);
      }
    };
    void fetchJDs();
  }, []);

  useEffect(() => {
    const fetchProcessingCvs = async (withLoading = false) => {
      if (isLoadingProcessingCvs) return;
      try {
        if (withLoading) {
          setIsLoadingProcessingCvs(true);
        }
        const response = await getUploadCv({
          page: processingPage,
          page_size: PAGE_SIZE,
          mine_only: false,
        });
        setProcessingCvs(response?.items || []);
        setTotalProcessingCvs(response?.pagination?.total || 0);
      } catch (e) {
        console.error(e);
        // setProcessingCvs([])
        // setTotalProcessingCvs(0)
      } finally {
        if (withLoading) {
          setIsLoadingProcessingCvs(false);
        }
      }
    };
    const interval = setInterval(async () => {
      fetchProcessingCvs();
    }, TIME_INTERVAL);
    void fetchProcessingCvs(true);
    return () => {
      clearInterval(interval);
    };
  }, [processingPage]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError(null);
    const newFiles: {
      uuid: string;
      file: File;
    }[] = [];
    const newProgress: Record<string, number> = {};
    const newStatus: Record<string, UploadStatus> = {};
    for (const file of acceptedFiles) {
      const uuid = crypto.randomUUID();
      newFiles.push({
        uuid,
        file,
      });
      ((newProgress[uuid] = 0), (newStatus[uuid] = 'READY'));
    }

    setFiles((prev) => [...prev, ...newFiles]);
    setStatusMap((_prev) => ({
      ..._prev,
      ...newStatus,
    }));
  }, []);

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    setError(getUploadRejectionMessage(fileRejections));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: true,
    disabled: isAnalyzing,
    minSize: 1,
    maxSize: 10 * 1024 * 1024,
    maxFiles: 50,
  } as any);

  const removeFileByUUID = useCallback((uuid: string) => {
    setFiles((_prev) => _prev.filter((file) => file.uuid !== uuid));
    setStatusMap((_prev) => {
      const newMap = { ..._prev };
      delete newMap[uuid];
      return newMap;
    });
  }, []);

  const handleUpload = useEffectEvent(async () => {
    if (files.length === 0 || !selectedJdId || !selectedSource || !selectedChannel) {
      setError('Please select a position, source, channel, and upload at least one CV.');
      return;
    }

    if (files.length > 50) {
      setError('You can upload a maximum of 50 files.');
      return;
    }

    const largeFiles = files.filter(
      (file) => file.file.size > 10 * 1024 * 1024, // > 10MB
    );

    if (largeFiles.length > 0) {
      const fileNames = largeFiles.map((f) => f.file.name).join(', ');
      setError(`These files exceed 10MB: ${fileNames}`);
      return;
    }

    const filesToUpload = [...files];

    if (filesToUpload.some((file) => file.file.size === 0)) {
      setError('Uploaded file is empty. Please select a valid file.');
      return;
    }

    const bodyFiles = await Promise.all(
      filesToUpload.map(async (file) => ({
        id: file.uuid,
        original_file_name: file.file.name,
        file_size: file.file.size,
        content_type: file.file.type,
        checksum_sha256: await sha256(file.file),
      })),
    );

    const body: UploadCVBody = {
      jd_id: selectedJdId,
      source: selectedSource,
      channel: selectedChannel,
      files: bodyFiles,
    };

    try {
      setError(null);
      setIsAnalyzing(true);
      const response = await postUploadCV(body);

      const items = response?.items || [];
      if (items.length !== filesToUpload.length) {
        throw new Error('Upload initialization returned an unexpected number of files.');
      }

      const tasks = items.map((item) => {
        const rawFile = filesToUpload.find((file) => file.uuid === item.resume_id)?.file;
        if (!rawFile) {
          throw new Error(`Missing local file for upload item ${item.resume_id}.`);
        }
        return retry({
          callback: async () => {
            setStatusMap((_prev) => ({
              ..._prev,
              [item.resume_id]: 'PROCESSING',
            }));
            await putUploadBlob(item.upload.upload_url, rawFile);
            setStatusMap((_prev) => ({
              ..._prev,
              [item.resume_id]: 'SUCCESS',
            }));
          },
          onFailed: () =>
            setStatusMap((_prev) => ({
              ..._prev,
              [item.resume_id]: 'FAILED',
            })),
        });
      });

      await Promise.allSettled(tasks);
    } catch (err) {
      const message = getUserFacingUploadErrorMessage(err);
      setError(message);
      console.error(err);
    } finally {
      setIsAnalyzing(false);
    }
  });

  const getIconStatus = useCallback((status: UploadStatus) => {
    switch (status) {
      case 'READY':
        return <BadgeCheck className="text-muted-foreground" size={16} />;

      case 'PROCESSING':
        return <Loader2 className="animate-spin text-primary" size={16} />;

      case 'SUCCESS':
        return <UploadCloud className="text-success" size={16} />;

      case 'FAILED':
        return <XCircle className="text-destructive" size={16} />;

      default:
        return <XCircle className="text-destructive" size={16} />;
    }
  }, []);

  const isReadyForUpload = useMemo(
    () => Object.values(statusMap).findIndex((e) => e !== 'READY'),
    [statusMap],
  );

  const handleClearInput = useCallback(() => {
    setSelectedJdId('');
    setSelectedSource('');
    setSelectedChannel('');
    setFiles([]);
    setStatusMap({});
  }, []);

  const SkeletonRow = useMemo(
    () => (
      <TableRow className="border-b border-border-subtle/50 animate-pulse">
        {/* CV Name */}
        <TableCell className="px-6 py-4 text-left">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-muted rounded shrink-0" />
            <div className="h-4 w-40 bg-muted rounded" />
          </div>
        </TableCell>

        {/* Position */}
        <TableCell className="px-6 py-4 text-left">
          <div className="h-4 w-32 bg-muted rounded mx-auto md:mx-0" />
        </TableCell>

        {/* Uploaded At */}
        <TableCell className="px-6 py-4 text-center">
          <div className="h-4 w-24 bg-muted rounded mx-auto" />
        </TableCell>

        {/* Status */}
        <TableCell className="px-6 py-4 text-center">
          <div className="h-6 w-20 bg-muted rounded-full mx-auto" />
        </TableCell>

        {/* Note */}
        <TableCell className="px-6 py-4 text-center">
          <div className="h-5 w-5 bg-muted rounded mx-auto" />
        </TableCell>

        {/* Actions */}
        <TableCell className="px-6 py-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-6 bg-muted rounded-md" />
            <div className="w-6 h-6 bg-muted rounded-md" />
          </div>
        </TableCell>
      </TableRow>
    ),
    [],
  );

  const handleClickDownloadCv = async (resumeId: string) => {
    try {
      const response = await getDownloadCVUrl(resumeId);
      window.open(response.download_url, '_blank');
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="space-y-8 max-w-6xl mx-auto pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-main flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <BrainCircuit className="text-primary" size={24} />
            </div>
            AI CV Scanner
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <div className="space-y-6">
          <div className="bg-card p-6 rounded-xl border border-border-subtle shadow-sm space-y-6">
            <div>
              <label className="block text-base font-semibold text-text-main mb-3">
                Select Target Position <span className="text-destructive">*</span>
              </label>
              <Select
                value={selectedJdId}
                onValueChange={setSelectedJdId}
                disabled={isAnalyzing || isLoadingJds || !isReadyForUpload}
              >
                <SelectTrigger className="w-full bg-bg-main">
                  <SelectValue placeholder="Choose a position..." />
                </SelectTrigger>
                <SelectContent>
                  {jds.map((jd) => (
                    <SelectItem key={jd.id} value={jd.id}>
                      {jd.title} {jd.department ? `(${jd.department})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-base font-semibold text-text-main mb-3">
                  Source <span className="text-destructive">*</span>
                </label>
                <Select
                  value={selectedSource}
                  onValueChange={setSelectedSource}
                  disabled={isAnalyzing || !isReadyForUpload}
                >
                  <SelectTrigger className="w-full bg-bg-main">
                    <SelectValue placeholder="Choose a source..." />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCE_OPTIONS.map((source) => (
                      <SelectItem key={source} value={source}>
                        {source}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-base font-semibold text-text-main mb-3">
                  Channel <span className="text-destructive">*</span>
                </label>
                <Select
                  value={selectedChannel}
                  onValueChange={setSelectedChannel}
                  disabled={isAnalyzing || !isReadyForUpload}
                >
                  <SelectTrigger className="w-full bg-bg-main">
                    <SelectValue placeholder="Choose a channel..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CHANNEL_OPTIONS.map((channel) => (
                      <SelectItem key={channel} value={channel}>
                        {channel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-base font-semibold text-text-main mb-3">
                Upload Candidate CV
              </label>
              {isReadyForUpload ? (
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
                    isAnalyzing
                      ? 'border-border-subtle pointer-events-none'
                      : isDragActive
                        ? 'border-primary bg-primary-light'
                        : 'border-border-subtle hover:border-primary hover:bg-bg-main'
                  }`}
                >
                  <input {...getInputProps()} />
                  <div className="w-12 h-12 bg-bg-main rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileUp className="text-primary" size={24} />
                  </div>
                  <p className="text-base font-semibold text-text-main">
                    Drop CV file here or click to browse
                  </p>
                  <p className="text-sm text-text-subtle mt-2">
                    Supports PDF, DOCX, TXT, XLSX (Max 10MB)
                  </p>
                </div>
              ) : null}
              {files.length > 0 && (
                <div className="border-2 border-dashed border-border-subtle rounded-xl p-2 mt-2 max-h-90 overflow-y-auto">
                  {files.map((file) => {
                    const status = statusMap[file.uuid];
                    const icon = getIconStatus(status || 'FAILED');
                    return (
                      <div
                        key={file.uuid}
                        className="mt-2 bg-bg-main p-4 rounded-lg border border-border-subtle flex items-center justify-between relative overflow-hidden"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-card rounded-md shadow-sm">
                            <FileText className="text-primary" size={18} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-base font-semibold text-text-main truncate max-w-45">
                              {file.file.name}
                            </p>
                            <p className="text-xs text-text-subtle uppercase font-bold tracking-wider">
                              {icon}
                            </p>
                          </div>
                        </div>
                        {status === 'READY' && (
                          <Button
                            onClick={() => {
                              removeFileByUUID(file.uuid);
                            }}
                            variant="ghost"
                            size="icon-sm"
                            className="p-1.5 hover:bg-border-subtle rounded-full transition-colors text-text-subtle"
                          >
                            <X size={14} />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {isReadyForUpload ? (
              <Button
                onClick={handleUpload}
                disabled={
                  files.length === 0 ||
                  !selectedJdId ||
                  !selectedSource ||
                  !selectedChannel ||
                  isAnalyzing
                }
                className="w-full py-3 font-semibold"
              >
                <Sparkles size={18} />
                <span>{isAnalyzing ? 'Analyzing...' : 'Analyze CV with AI'}</span>
              </Button>
            ) : (
              <Button onClick={handleClearInput} className="w-full py-3 font-semibold">
                <UploadCloud size={18} />
                <span>Upload another file</span>
              </Button>
            )}

            {error && (
              <div className="flex items-center gap-3 text-error-muted-foreground bg-error-muted p-4 rounded-lg text-base border border-error/30">
                <AlertCircle size={18} className="shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <div className="bg-info-muted border border-info/30 p-4 rounded-xl flex gap-3">
            <Info className="text-info shrink-0" size={18} />
            <div className="text-sm text-info-muted-foreground leading-relaxed">
              <span className="font-bold block mb-1">How it works</span>
              Our AI engine extracts key entities from the CV and compares them against the job
              description to calculate a suitability score and provide detailed insights.
            </div>
          </div>
        </div>
      </div>

      <section className="bg-card rounded-xl border border-border-subtle shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border-subtle flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-text-main">CV processing progress</h2>
            <p className="text-sm text-text-subtle mt-1">
              Track uploaded CVs and their processing state.
            </p>
          </div>
          <span className="text-xs font-bold uppercase tracking-widest text-text-subtle">
            {totalProcessingCvs} files
          </span>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-200">
            <TableHeader>
              <TableRow className="bg-bg-main/40 border-border-subtle/60 text-xs uppercase tracking-widest text-text-subtle">
                <TableHead className="px-6 py-3 font-bold">CV name</TableHead>
                <TableHead className="px-6 py-3 font-bold">Position</TableHead>
                <TableHead className="px-6 py-3 font-bold">Upload date</TableHead>
                <TableHead className="px-6 py-3 font-bold">Status</TableHead>
                <TableHead className="px-6 py-3 font-bold w-20 text-center">Note</TableHead>
                <TableHead className="px-6 py-3 font-bold">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingProcessingCvs ? (
                <>
                  {SkeletonRow}
                  {SkeletonRow}
                  {SkeletonRow}
                  {SkeletonRow}
                  {SkeletonRow}
                </>
              ) : (
                processingCvs.map((row) => (
                  <TableRow
                    key={row.resume_id}
                    className="border-b border-border-subtle/50 last:border-0 hover:bg-bg-main/20 transition-colors"
                  >
                    <TableCell className="px-6 py-4 text-left">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-primary shrink-0" />
                        <span className="text-sm font-medium text-text-main">{row.cv_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-left text-sm text-text-main">
                      {row.position}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-center text-sm text-text-subtle">
                      {new Date(row.uploaded_at).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-center">
                      <div className="flex flex-col items-center gap-1.5">
                        <Badge
                          variant="secondary"
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${processingStatusStyles[row.ai_status]}`}
                        >
                          {isProcessingStatus(row.ai_status) ? (
                            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                          ) : null}
                          {getProcessingStatusLabel(row.ai_status)}
                        </Badge>
                        {isProcessingStatus(row.ai_status) ? (
                          <div className="w-28 space-y-1" aria-label="AI processing indicator">
                            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                              <div className="h-full w-1/2 animate-pulse rounded-full bg-primary" />
                            </div>
                            <div className="text-[11px] font-medium text-text-subtle">
                              AI is calculating the score...
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-center">
                      {row.extraction_status === 'FAILED' ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center justify-center p-1 rounded-md text-warning-muted-foreground hover:bg-warning-muted transition-colors cursor-help"
                              aria-label="View a short note about this upload"
                            >
                              <FileCode size={18} strokeWidth={1.75} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="left"
                            className="max-w-xs text-left text-xs text-balance leading-relaxed"
                          >
                            {userFacingProcessingNote(row.extraction_failure_reason)}
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-center">
                      <Button
                        onClick={() => {
                          handleClickDownloadCv(row.resume_id);
                        }}
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className="p-1.5 rounded-md hover:bg-bg-main text-text-subtle hover:text-primary transition-colors"
                        title="Download CV"
                      >
                        <Download size={14} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="px-6 py-4 border-t border-border-subtle bg-bg-main/20 flex items-center justify-between">
          <div className="text-sm text-text-subtle">
            {totalProcessingCvs > 0 && (
              <>
                Showing{' '}
                <span className="font-semibold text-text-main">
                  {(processingPage - 1) * PAGE_SIZE + 1}
                </span>{' '}
                to{' '}
                <span className="font-semibold text-text-main">
                  {Math.min(processingPage * PAGE_SIZE, totalProcessingCvs)}
                </span>{' '}
                of <span className="font-semibold text-text-main">{totalProcessingCvs}</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-1">
            <Button
              type="button"
              onClick={() => setProcessingPage((page) => Math.max(1, page - 1))}
              disabled={processingPage === 1}
              variant="outline"
              size="icon-sm"
              className="p-1.5 rounded-md border border-border-subtle bg-card text-text-subtle hover:text-primary hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={14} />
            </Button>
            {visibleProcessingPages.map((item, index) =>
              item === 'ellipsis' ? (
                <span
                  key={`ellipsis-${index}`}
                  className="flex h-8 min-w-8 items-center justify-center text-sm font-semibold text-text-subtle"
                >
                  ...
                </span>
              ) : (
                <Button
                  key={item}
                  type="button"
                  onClick={() => setProcessingPage(item)}
                  variant={item === processingPage ? 'primary' : 'outline'}
                  size="sm"
                  className={`min-w-8 h-8 rounded-md border text-sm font-semibold transition-colors ${
                    item === processingPage
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border-subtle bg-card text-text-subtle hover:border-primary hover:text-primary'
                  }`}
                >
                  {item}
                </Button>
              ),
            )}
            <Button
              type="button"
              onClick={() => setProcessingPage((page) => Math.min(totalProcessingPages, page + 1))}
              disabled={processingPage >= totalProcessingPages}
              variant="outline"
              size="icon-sm"
              className="p-1.5 rounded-md border border-border-subtle bg-card text-text-subtle hover:text-primary hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
